// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Glint — the palette. Every colour that carries meaning on the play surface lives here so
// contrast.test.ts can pin it to WCAG 1.4.11 (≥3:1 for a non-text graphic) against the
// surface it is actually drawn on. Symbol identity is carried by SHAPE (see glyphs.ts);
// the six accent hues are a redundant, colour-blind-safe cue (Okabe–Ito derived, darkened
// so each clears 3:1 on the parchment card), never the sole way to tell two symbols apart.

export const PALETTE = {
  stage: '#12151c', // the dark board
  stageEdge: '#0b0d13', // the darker vignette / border ring a card can sit against
  panel: '#1b2029', // HUD chips / sheets
  card: '#f2ece0', // the parchment card a glyph is drawn on
  cardEdge: '#cdbfa6', // the card's rim
  cardInk: '#241f18', // dark ink for text/markers ON the card

  hud: '#e9eef6', // HUD text on the stage
  hudDim: '#9aa6b6',

  good: '#39b06e', // correct flash (on stage)
  bad: '#e0503b', // wrong flash (on stage)
  spark: '#f3ad34', // combo / glint accent (on stage)

  // Six glyph accent hues — each ≥3:1 on `card`. Identity is shape; colour is redundant.
  accents: ['#166b60', '#b0530d', '#2f519e', '#a83226', '#4f6b16', '#6a3a9c'] as const,
} as const;

export type Accent = (typeof PALETTE.accents)[number];

/** Deterministic accent for a symbol id — stable across peers, redundant with shape. */
export function accentFor(symbolId: number): string {
  return PALETTE.accents[symbolId % PALETTE.accents.length];
}
